"use client";

import { useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/lib/auth-provider';

export interface DelayedCounterOptions {
  mode: 'classicMode' | 'multiGridMode';
  delayMs?: number; // Délai en millisecondes (défaut: 5 minutes)
}

/**
 * Hook pour gérer le comptage différé des grilles
 * Compte une grille seulement après 5 minutes d'inactivité
 * Incrémente aussi les statistiques utilisateur si connecté
 */
export function useDelayedGridCounter({ mode, delayMs = 5 * 60 * 1000 }: DelayedCounterOptions) {
  const { session } = useAuth();
  // Référence au timer actuel
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Référence pour savoir si un comptage est en attente
  const pendingCountRef = useRef<boolean>(false);
  // Référence pour éviter les appels multiples à l'API
  const isCountingRef = useRef<boolean>(false);

  // Fonction pour effectuer l'incrémentation
  const performIncrement = useCallback(async () => {
    if (isCountingRef.current) return;
    
    try {
      isCountingRef.current = true;
      
      // Ne pas compter en mode Electron (offline)
      const isElectron = typeof window !== 'undefined' && (window as any).electron;
      if (isElectron) {
        return;
      }
      
      // Incrémenter le compteur global
      await fetch('/api/grid-counters/increment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
      });

      // Incrémenter les stats utilisateur si connecté
      try {
        if (session) {
          await fetch('/api/user-stats/increment', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ statType: 'grids_generated', increment: 1 }),
          });
        }
      } catch (userStatError) {
        // Silent error - user stats are not critical
        console.debug('Could not increment user stats:', userStatError);
      }
    } catch (error) {
      // Silent error
    } finally {
      isCountingRef.current = false;
      pendingCountRef.current = false;
    }
  }, [mode, session]);

  // Fonction pour annuler le timer actuel
  const cancelTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingCountRef.current = false;
  }, []);

  // Fonction pour démarrer/redémarrer le timer
  const startTimer = useCallback(() => {
    // Annuler le timer existant s'il y en a un
    cancelTimer();
    
    // Démarrer un nouveau timer
    pendingCountRef.current = true;
    
    timerRef.current = setTimeout(() => {
      performIncrement();
    }, delayMs);
  }, [cancelTimer, delayMs, mode, performIncrement]);

  // Fonction principale : signaler une activité (génération ou modification)
  const onActivity = useCallback((isGeneration = false) => {
    if (isGeneration) {
      // Nouvelle génération de grille - démarrer le timer
      startTimer();
    } else {
      // Modification - redémarrer le timer si un comptage est en attente
      if (pendingCountRef.current) {
        startTimer();
      }
    }
  }, [mode, startTimer]);

  // Fonction pour forcer le comptage immédiat (cas spéciaux)
  const forceCount = useCallback(async () => {
    cancelTimer();
    await performIncrement();
  }, [cancelTimer, mode, performIncrement]);

  // Fonction pour vérifier si un comptage est en attente
  const isPending = useCallback(() => {
    return pendingCountRef.current;
  }, []);

  // Nettoyer le timer au démontage du composant
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    onActivity,     // Signaler une activité (génération ou modification)
    forceCount,     // Forcer le comptage immédiat 
    cancelTimer,    // Annuler le timer
    isPending,      // Vérifier si un comptage est en attente
  };
}
